import { useState, type FunctionComponent } from 'react';
import { type LocationTypeEnum } from '../../../__generated__/graphql-client-types';
import { getHumanReadableLocationType, makeShowroomLocationLink } from '../../../helpers/showroom/showroom.helper';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { type ShowroomAddress } from '../../../types/showroom.types';
import { ClickableElement } from '../../buttons';
import { LinkButton } from '../../buttons/link-button/link-button.component';
import { HelpIcon } from '../../common-components/help-icon/help-icon.component';
import { Link } from '../../common-components/link/link.component';
import { LoadingWrapper } from '../../loading-wrapper/loading-wrapper.component';

const SELECTION_CENTER_MESSAGE = 'Our selection centers offer a limited product line but all of our product expertise';

export type SelectedShowroomDetails = {
	address: ShowroomAddress;
	locationType: LocationTypeEnum;
	branchPageUrl: string;
};

export type SearchResultCardProps = {
	isLastLocation: boolean;
	locationType: LocationTypeEnum;
	address: ShowroomAddress;
	branchPageUrl: string;
	searchResultsLoading?: boolean;
};

export const SearchResultCard: FunctionComponent<SearchResultCardProps> = ({
	isLastLocation,
	locationType,
	address,
	branchPageUrl,
	searchResultsLoading = false
}) => {
	const [isHovered, setHovered] = useState<boolean>(false);
	const navigate = useNavigation();
	const addressSearchString = `${address.addressLine1}, ${address.addressLine2 && address.addressLine2 + ', '}${address.city}, ${
		address.state
	} ${address.zipCode}`;
	const label = getHumanReadableLocationType(locationType) || '';
	return (
		<ClickableElement
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			ariaLabel="showroom search result card"
			data-testid="showroom-search-result"
			onClick={() => navigate(makeShowroomLocationLink(branchPageUrl))}>
			<>
				<Link className="theme-grey-darker" url={makeShowroomLocationLink(branchPageUrl)}>
					<h2 className={`ma0 mb1 f4 f3-ns lh-title font-medium ${isHovered && 'underline'}`} data-testid="showroom-card-header">
						<LoadingWrapper isLoading={searchResultsLoading} height={12} width={60}>
							{address.city}, {address.state}
						</LoadingWrapper>
					</h2>
				</Link>
				<span className="flex f5 f4-ns lh-title ma0">
					<LoadingWrapper isLoading={searchResultsLoading} height={10} width={200}>
						{locationType === 'SELECTION_CENTER' ? (
							<ClickableElement ariaLabel={'selection center message'} onClick={(e) => e.stopPropagation()}>
								<span className="flex">
									{label}
									<HelpIcon className="w5 f6 lh-copy theme-grey-darker" iconClassName="pl1 f5">
										{SELECTION_CENTER_MESSAGE}
									</HelpIcon>
								</span>
							</ClickableElement>
						) : (
							label
						)}
					</LoadingWrapper>
				</span>

				<div className="mv3 theme-grey f6">
					<div className={`${searchResultsLoading ? 'mb1' : ''}`}>
						<LoadingWrapper isLoading={searchResultsLoading} height={8} width={100}>
							{address.addressLine1}
						</LoadingWrapper>
					</div>
					{address.addressLine2 && (
						<div>
							<LoadingWrapper isLoading={searchResultsLoading} height={8} width={100}>
								{address.addressLine2}
							</LoadingWrapper>
						</div>
					)}
					<div>
						<LoadingWrapper isLoading={searchResultsLoading} height={8} width={100}>
							{address.city}, {address.state} {address.zipCode}
						</LoadingWrapper>
					</div>
				</div>

				<p className="theme-grey f6">
					<LoadingWrapper isLoading={searchResultsLoading} height={8} width={100}>
						Phone: {address.phoneNumber}
					</LoadingWrapper>
				</p>

				<div className="flex items-center">
					<LoadingWrapper isLoading={searchResultsLoading} height={24} width={100}>
						<LinkButton ariaLabel="Book Appointment Button" url={makeShowroomLocationLink(branchPageUrl)}>
							Plan Your Visit
						</LinkButton>
					</LoadingWrapper>
					<a
						href={`https://www.google.com/maps/place/${encodeURIComponent(addressSearchString)}`}
						className="ml3 link theme-primary underline-hover"
						target="_blank"
						rel="noreferrer"
						onClick={(event) => event.stopPropagation()}>
						<LoadingWrapper isLoading={searchResultsLoading} height={10} width={50}>
							Directions
						</LoadingWrapper>
					</a>
				</div>

				{!isLastLocation && <hr className="bn bg-theme-grey-light mt3 mb5" style={{ height: '1px' }} />}
			</>
		</ClickableElement>
	);
};
